import { gql, useMutation, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import getFeature from '../../utils/features';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import type {
  OrganizationAllowedEmbedHostsQuery,
  OrganizationAllowedEmbedHostsQueryVariables,
  UpdateAllowedEmbedHostsMutation,
  UpdateAllowedEmbedHostsMutationVariables,
} from '../../graphql/generated/graphqlTypes';
import UpgradeButton from '../../organization/UpgradeButton';
import SettingCard from '../../settings/SettingCard';
import nonEmpty from '../../utils/nonEmpty';
import styles from './PublicContentSettings.module.scss';

const OPTIONS = {
  ALLOW_ALL: 'Allow all iframe hosts',
  ALLOW_SPECIFIC: 'Allow specific iframe hosts',
  DISABLED: 'Disable unintegrated iframe hosts',
} as const;

export const ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY = gql`
  query organizationAllowedEmbedHosts {
    session: currentSession {
      organization {
        id
        settings {
          allowedEmbedHosts
        }
      }
    }
  }
`;

const UPDATE_ALLOWED_EMBED_HOSTS_MUTATION = gql`
  mutation updateAllowedEmbedHosts($allowedEmbedHosts: [String!]!) {
    organization: updateOrganization(
      settings: { allowedEmbedHosts: $allowedEmbedHosts }
    ) {
      id
      settings {
        allowedEmbedHosts
      }
    }
  }
`;

type Values = {
  allowed: keyof typeof OPTIONS;
  hosts: string;
};

export const parseHosts = (hostsString: string | null): string[] =>
  hostsString
    ?.split(',')
    .map((host) => {
      try {
        const trimmed = host.trim();
        return new URL(
          /^https?:\/\//.test(trimmed) ? trimmed : `http://${trimmed}`,
        ).host;
      } catch (e) {
        return '';
      }
    })
    .filter(Boolean) ?? [];

const joinHosts = (hosts: Array<string | null | undefined> | null): string =>
  hosts?.filter(nonEmpty).join(', ') ?? '';

const AllowedIframeHostsSettings = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { data } = useQuery<
    OrganizationAllowedEmbedHostsQuery,
    OrganizationAllowedEmbedHostsQueryVariables
  >(ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY);

  const [updateEmbedHosts] = useMutation<
    UpdateAllowedEmbedHostsMutation,
    UpdateAllowedEmbedHostsMutationVariables
  >(UPDATE_ALLOWED_EMBED_HOSTS_MUTATION);

  const handleSubmit = async (values: Values) => {
    if (values.allowed === 'ALLOW_ALL') {
      await updateEmbedHosts({ variables: { allowedEmbedHosts: ['*'] } });
    } else if (values.allowed === 'DISABLED') {
      await updateEmbedHosts({ variables: { allowedEmbedHosts: [] } });
    } else {
      await updateEmbedHosts({
        variables: { allowedEmbedHosts: parseHosts(values.hosts) },
      });
    }
    setIsEditing(false);
  };

  const isEditorIFrameEnabled = getFeature('editor_iframe');

  const initialValues: Values = useMemo<Values>(() => {
    const allowedEmbedHosts =
      data?.session?.organization?.settings?.allowedEmbedHosts;
    if (
      !allowedEmbedHosts ||
      allowedEmbedHosts.length === 0 ||
      !isEditorIFrameEnabled
    ) {
      return { allowed: 'DISABLED', hosts: '' };
    }
    if (allowedEmbedHosts.length === 1 && allowedEmbedHosts[0] === '*') {
      return { allowed: 'ALLOW_ALL', hosts: '' };
    }
    return { allowed: 'ALLOW_SPECIFIC', hosts: joinHosts(allowedEmbedHosts) };
  }, [data, isEditorIFrameEnabled]);

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Allowed iframe hosts"
          description={
            <Form<Values>
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange
              validate={(values) =>
                values.allowed === 'ALLOW_SPECIFIC' &&
                parseHosts(values.hosts).length === 0
                  ? { hosts: 'Please include at least one host' }
                  : {}
              }
            >
              {({ values }) => (
                <>
                  <div className={styles.formFields}>
                    <Field
                      label={OPTIONS.ALLOW_ALL}
                      name="allowed"
                      type="radio"
                      value="ALLOW_ALL"
                    />
                    <Field
                      label={OPTIONS.ALLOW_SPECIFIC}
                      name="allowed"
                      type="radio"
                      value="ALLOW_SPECIFIC"
                    />
                    {values.allowed === 'ALLOW_SPECIFIC' && (
                      <Field
                        required
                        type="text"
                        name="hosts"
                        placeholder="Enter hosts separated by commas (e.g. share.getcloudapp.com, loom.com)"
                      />
                    )}
                    <Field
                      label={OPTIONS.DISABLED}
                      name="allowed"
                      type="radio"
                      value="DISABLED"
                    />
                  </div>
                  <div className={styles.formFooter}>
                    <Submit variant="primary">Save</Submit>
                  </div>
                </>
              )}
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            isEditorIFrameEnabled ? (
              <Button onClick={() => setIsEditing(true)} variant="secondary">
                Edit
              </Button>
            ) : (
              <UpgradeButton feature="customEmbedHosts">Upgrade</UpgradeButton>
            )
          }
          title="Allowed iframe hosts"
          description={OPTIONS[initialValues.allowed]}
        />
      )}
    </>
  );
};

export default AllowedIframeHostsSettings;
